import { Component, EventEmitter, Input, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR, ValidatorFn, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

@Component({
  selector: 'telio-auto-complete',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutoCompleteComponent),
      multi: true,
    },
  ],

})
export class AutoCompleteComponent implements OnInit, ControlValueAccessor {
  @Input() label: string;
  @Input() options: any[];
  @Input() valueProperty: string;
  @Input() emitValueProperty: string;
  @Input() width: string;
  @Output() valueSelected = new EventEmitter<any>();
  @Input() required: boolean = false;
  @ViewChild(MatAutocompleteTrigger, { static: false }) autocompleteTrigger: MatAutocompleteTrigger;
  statusChanges$: Observable<any>;
  private onChange: (value: any) => void = () => { };
  private onTouch: () => void = () => { };

  autocompleteControl: UntypedFormControl = new UntypedFormControl('', this.getValidator());
  filteredOptions: any[] = [];
  selectedDisplayValue: string;
  selectedValue: any;

  constructor(readonly translate: TranslateService) {
    this.autocompleteControl.valueChanges.subscribe(value => {
      this.filterOptions(value);
    });
  }

  ngOnInit(): void {
  }
  getValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      if (this.required && !this.isValidValue(value)) {
        return { invalidValue: true };
      }
      return null;
    };
  }

  isValidValue(value: any): boolean {
    return this.options.some(option => option[this.valueProperty] === value);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.selectedDisplayValue = event.option.viewValue;
    this.autocompleteControl.setValue(this.selectedDisplayValue);
    this.selectedValue = event.option.value[this.emitValueProperty];
    this.valueSelected.emit(this.selectedValue);
    this.onChange(event.option.value[this.emitValueProperty]);
    this.onTouch();
    this.autocompleteTrigger.panelClosingActions
      .subscribe(e => {
        this.autocompleteControl.setValue(null);
      });
  }

  filterOptions(value: string): void {
    const filterValue = (value || '').toString().toLowerCase();
    this.filteredOptions = this.options.filter(option => {
      const optionValue = (option[this.valueProperty] || '').toString().toLowerCase();
      return optionValue.includes(filterValue);
    });

  }

  openAutocompletePanel() {
    if (this.autocompleteTrigger && !this.autocompleteTrigger.panelOpen) {
      if (this.autocompleteControl.value) {
        this.autocompleteControl.setErrors(null);
        this.autocompleteTrigger.openPanel();
      } else {
        this.autocompleteTrigger._onChange("");
        this.autocompleteTrigger.openPanel();
      }
    }
  }

  writeValue(obj: any): void {
    this.autocompleteControl.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.autocompleteControl.disable();
    } else {
      this.autocompleteControl.enable();
    }
    this.onChange(this.autocompleteControl.value);
    this.onTouch();
  }

  onInputChange(value: string): void {
    this.onChange(value);
    this.onTouch();
    const isValidOption = this.options.some(option => option[this.valueProperty] === value);
    if (this.required && !isValidOption) {
    this.autocompleteControl.setErrors({ required: true });
    this.selectedValue = null; 
    this.valueSelected.emit(null); 
    this.autocompleteControl.markAsDirty();
    this.autocompleteControl.markAsTouched();
    this.autocompleteControl.updateValueAndValidity();
  } else {
    this.autocompleteControl.setErrors(null);
  }

  }
}
